import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dumbbell, HeartPulse, Salad } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { BrandLockup, BrandLockupImage } from "@/components/BrandMark";
import { Reveal } from "@/components/common";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const me = isLogin ? await login(f.email, f.password) : await register(f.name, f.email, f.password);
      if (me.role === "admin") nav(me.onboarding_complete && me.modules ? "/" : "/admin");
      else nav(me.onboarding_complete ? "/" : "/bienvenida");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative h-56 overflow-hidden bg-gradient-to-br from-ink via-[#0F4C81] to-train lg:h-auto">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,.25), transparent 45%), radial-gradient(circle at 80% 70%, rgba(232,245,238,.35), transparent 40%)" }} />
        <div className="absolute bottom-6 left-6 right-6 text-white lg:bottom-12 lg:left-12">
          <BrandLockupImage className="mb-4 h-14 w-auto max-w-[260px] drop-shadow-lg sm:h-16 sm:max-w-[320px]" />
          <h2 className="mt-2 max-w-md font-display text-2xl font-extrabold sm:text-4xl">Tres pilares, un solo lugar para moverte, alimentarte y cuidarte.</h2>
          <div className="mt-4 hidden gap-2 sm:flex">
            {[[Dumbbell, "bg-train"], [Salad, "bg-food"], [HeartPulse, "bg-care"]].map(([I, c], i) => (
              <span key={i} className={`flex h-10 w-10 items-center justify-center rounded-2xl text-white ${c}`}><I className="h-5 w-5" /></span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-10">
        <Reveal className="w-full max-w-sm">
          <BrandLockup useImage compact />
          <h1 className="mt-6 text-3xl font-extrabold sm:text-4xl" data-testid="auth-title">{isLogin ? "Hola de nuevo" : "Crea tu cuenta"}</h1>
          <p className="mt-2 text-sm text-stone-600">{isLogin ? "Entra para ver tu plan de hoy." : "En 3 minutos tendrás tu plan personalizado."}</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {!isLogin && (
              <Input required placeholder="Tu nombre" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-12 rounded-2xl" data-testid="auth-name-input" />
            )}
            <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-12 rounded-2xl" data-testid="auth-email-input" />
            <Input required type="password" minLength={6} placeholder="Contraseña (mín. 6 caracteres)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-12 rounded-2xl" data-testid="auth-password-input" />
            {isLogin && (
              <div className="text-right">
                <Link to="/recuperar" className="text-sm font-semibold text-train-ink underline-offset-4 hover:underline" data-testid="auth-forgot-link">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            )}
            {error && <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="auth-error">{error}</p>}
            <button disabled={busy} data-testid="auth-submit-btn" className="w-full rounded-full bg-ink py-3.5 font-semibold text-white transition-transform active:scale-95 disabled:opacity-60">
              {busy ? "Un momento…" : isLogin ? "Entrar" : "Crear cuenta"}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-stone-600">
            {isLogin ? "¿No tienes cuenta? " : "¿Ya tienes cuenta? "}
            <Link to={isLogin ? "/registro" : "/login"} className="font-semibold text-train-ink underline-offset-4 hover:underline" data-testid="auth-switch-link">
              {isLogin ? "Regístrate" : "Inicia sesión"}
            </Link>
          </p>
        </Reveal>
      </div>
    </div>
  );
}
